import React, { createContext, useState, useEffect, useContext, useCallback, lazy, Suspense } from 'react';
import { AIContext } from './AIContext';
import { Drawer } from 'components/ui';

// Lazy load ImageChat to avoid circular dependency
const ImageChat = lazy(() => import('components/image-chat/ImageChat'));

export const ImageChatContext = createContext({
    selectedModel: null,
    setSelectedModel: () => {},
    editAIModel: null,
    openImageChat: () => {},
    closeImageChat: () => {},
    hasEditModel: false,
    isOpen: false
});

/**
 * Hook to use ImageChat context
 */
export const useImageChat = () => {
    const context = useContext(ImageChatContext);
    if (!context) {
        throw new Error('useImageChat must be used within ImageChatProvider');
    }
    return context;
};

export const ImageChatProvider = ({ children }) => {
    const { aiImageModels } = useContext(AIContext);
    
    // Get first available image edit model
    const editableModels = aiImageModels?.filter(m => 
        m.allowImageEdit && !m.disabled && !m.hidden
    ) || [];
    
    const [selectedModel, setSelectedModel] = useState(editableModels[0]?.model || null);
    const [isOpen, setIsOpen] = useState(false);
    const [chatConfig, setChatConfig] = useState({
        initialImages: [],
        title: 'Image Chat',
        placeholder: 'Describe how you want to modify these images...',
        maxImages: 16,
        aspectRatio: '1:1',
        showHeader: true,
        isSelectable: false
    });
    
    // Update selected model if it becomes unavailable
    useEffect(() => {
        const currentModel = editableModels.find(m => m.model === selectedModel);
        if (!currentModel && editableModels.length > 0) {
            setSelectedModel(editableModels[0].model);
        }
    }, [aiImageModels, selectedModel, editableModels]);
    
    const editAIModel = editableModels.find(m => m.model === selectedModel) || editableModels[0];
    const hasEditModel = editableModels.length > 0;
    
    /**
     * Open ImageChat in a drawer
     * @param {Array} initialImages - Array of image URLs to start with
     * @param {Object} config - Configuration object
     */
    const openImageChat = useCallback((initialImages = [], config = {}) => {
        setChatConfig({
            initialImages: initialImages || [],
            title: config.title || 'Image Chat',
            placeholder: config.placeholder || 'Describe how you want to modify these images...',
            maxImages: config.maxImages || 16,
            aspectRatio: config.aspectRatio || '1:1',
            showHeader: config.showHeader !== undefined ? config.showHeader : true,
            isSelectable: config.isSelectable || false,
            onImageSelect: config.onImageSelect,
            onImageUnselect: config.onImageUnselect,
            externalSelectedImages: config.externalSelectedImages
        });
        setIsOpen(true);
    }, []);
    
    /**
     * Close ImageChat drawer
     */
    const closeImageChat = useCallback(() => {
        setIsOpen(false);
    }, []);
    
    return (
        <ImageChatContext.Provider value={{
            selectedModel,
            setSelectedModel,
            editAIModel,
            openImageChat,
            closeImageChat,
            hasEditModel,
            isOpen
        }}>
            {children}
            
            {/* Global ImageChat Drawer */}
            <Drawer
                isOpen={isOpen}
                onClose={closeImageChat}
                width="35vw"
                placement="right"
                className="image-chat-drawer"
                headerClass='!hidden'
                bodyClass='!p-0'
            >
                <Suspense fallback={<div className="flex items-center justify-center h-full">Loading...</div>}>
                    <ImageChat
                        {...chatConfig}
                        onClose={closeImageChat}
                        className="h-full"
                    />
                </Suspense>
            </Drawer>
        </ImageChatContext.Provider>
    );
};
